import { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from "raviger";
import ReactCodeInput from 'react-verification-code-input';
import { Box, Link, Typography, Card, Container, Button } from '@mui/material';

import ReLinkCounter from '../Common/ReLinkCounter.jsx';
import { useStyles } from '../../assets/LoginStyle.jsx';
import { VBox } from '../../assets/SharedComponents.jsx';
import { useSnackbar } from 'notistack';
import { RegisterContext } from 'context/registerContext.js';
import { generateVerificationEmailAuth0 } from 'api/api.js';
import { useTranslation } from 'react-i18next';
import { CustomBusy } from 'Components/Common/CustomBusy.jsx';
import getSMS from '../../assets/getSMS.svg';
import wrongSmsCode from '../../assets/wrongSmsCode.svg';
import { feathersClient } from 'api/client';
import usePhoneVerification from 'hooks/usePhoneVerification';
import { system } from 'api/client';

export const SmsVerificationReg = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { userObject } = useContext(RegisterContext);
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const { sendOTP, verifyOTP, loading } = usePhoneVerification();

    const [wrongPinInput, setwrongPinInput] = useState(false);
    const [verProperties, setverProperties] = useState({
        Resending: false,
        phoneText: ''
    });
    const [busy, setBusy] = useState(false);
    const [visible, setVisible] = useState(false);
    const pinInputRef = useRef(null);

    useEffect(() => {
        if (userObject.phone) {
            updatePageText();
            handleSendCode();
        } else {
            navigate('/fparent');
        }
    }, []);

    const updatePageText = () => {
        const phoneText = `0${userObject.phone}`;
        setverProperties({
            Resending: false,
            phoneText: phoneText
        });
    };

    const handleSendCode = async () => {
        try {
            console.log('📱 Sending OTP via Auth0...');

            setBusy(true);

            await sendOTP(userObject.phone);

            console.log('✅ OTP sent successfully');

        } catch (error) {
            console.error('❌ Send OTP failed:', error);
            // Error already shown by hook
        } finally {
            setBusy(false);
        }
    };

    const handleResending = async () => {
        setverProperties({ ...verProperties, Resending: true });
        setVisible(false);
        setwrongPinInput(false);

        await handleSendCode();

        setverProperties({ ...verProperties, Resending: false });
    };

    const handleVerificationChange = async (value) => {
        setBusy(true);
        setwrongPinInput(false);

        try {
            // Development bypass code
            const bypassCode = process.env.REACT_APP_SMS_BYPASS_CODE || "111111";

            if (value === bypassCode) {
                console.log('🔓 Development bypass code used');
                await completeRegistration(null);
                return;
            }

            console.log('🔐 Verifying OTP...');
            const username = userObject.id.toString();
            // Verify OTP with Auth0 (isRegistration=true skips SAP lookup)
            const result = await verifyOTP(userObject.phone, value, username, true);

            console.log('✅ OTP verified successfully');

            // Complete registration with Auth0 token
            await completeRegistration(result);

        } catch (error) {
            console.error('❌ OTP verification failed:', error);
            setwrongPinInput(true);
            // Error already shown by hook
        } finally {
            setBusy(false);
            try {
                pinInputRef.current?.__clearvalues__();
            } catch (error) {
                // Ignore
            }
        }
    };

    // Sends the Auth0 verification email, retrying once on failure instead of
    // failing silently. If it still fails, the parent is warned so they know
    // to use the "didn't receive mail" resend link on the next screen, rather
    // than the previous behavior of silently never sending anything.
    const sendVerificationEmailWithRetry = async (payload, attempt = 1) => {
        try {
            await generateVerificationEmailAuth0(payload);
        } catch (error) {
            console.error(`❌ Failed to send verification email (attempt ${attempt}):`, error);

            if (attempt < 2) {
                await sendVerificationEmailWithRetry(payload, attempt + 1);
                return;
            }

            enqueueSnackbar(
                t("verificationEmailSendFailed") || "לא הצלחנו לשלוח את מייל האימות, ניתן לשלוח שוב מהמסך הבא",
                { variant: 'warning' }
            );
        }
    };

    const completeRegistration = async (otpResult) => {
        try {
            console.log('✅ SMS verified - completing registration...');

            const backendUrl = process.env[`REACT_APP_BACKEND_URL_${system.toUpperCase()}`] || 'http://localhost:3030';
            const sPhoneNum = userObject.phone.toString();
            const formattedPhone = sPhoneNum.startsWith('0')
                ? `+972${sPhoneNum.slice(1)}`
                : `+972${sPhoneNum}`;

            // Step 1: Create user in MongoDB and Auth0
            console.log('📝 Creating user in MongoDB and Auth0...');

            const registrationResponse = await fetch(`${backendUrl}/complete-registration-auth0`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: userObject.email,
                    username: userObject.id.toString(),
                    usernameType: userObject.idType,
                    password: userObject.password,
                    phone_number: formattedPhone,
                    maritalStatus: userObject.familyStatus,
                    firstName: userObject.firstName,
                    lastName: userObject.lastName,
                    street: userObject.street,
                    streetNo: userObject.streetNo,
                    city: userObject.city,
                    parent2Id: userObject.parent2Id || '',
                    parent2IdType: userObject.parent2IdType || '3',
                    parent2Phone: userObject.parent2Phone || '',
                    parent2FirstName: userObject.parent2FirstName || '',
                    parent2LastName: userObject.parent2LastName || '',
                    parent2Email: userObject.parent2Email || '',
                    parent2City: userObject.parent2City || '',
                    parent2Street: userObject.parent2Street || '',
                    parent2StreetNo: userObject.parent2StreetNo || ''
                })
            });

            const registrationData = await registrationResponse.json();

            if (!registrationResponse.ok) {
                throw new Error(registrationData.message || 'Registration failed');
            }

            console.log('✅ User created in MongoDB and Auth0');

            // Step 2: Authenticate with Feathers using Auth0 token
            console.log('🔐 Authenticating with Feathers...');

            const authResult = await feathersClient.authenticate({
                strategy: 'auth0',
                access_token: registrationData.access_token,
                username: userObject.id.toString(),
                phoneNumber: formattedPhone
            });

            console.log('✅ Authenticated with Feathers');

            // Step 3: Send verification email
            // Awaited (with a retry) and passed the auth0Id we just got back from
            // complete-registration-auth0, so the backend doesn't need to re-look-up
            // the just-created Auth0 user by email (that lookup can race Auth0's
            // read-after-write propagation and intermittently fail silently).
            console.log('📧 Sending verification email...');

            await sendVerificationEmailWithRetry({
                email: userObject.email,
                auth0Id: registrationData.auth0Id
            });

            console.log('✅ Registration complete!');

            // Navigate to email verification page
            navigate(`/mailver/${userObject.email}`);

        } catch (error) {
            console.error('❌ Registration error:', error);
            enqueueSnackbar(
                error.message || t("errorOccuredText") || "אירעה שגיאה",
                { variant: 'error' }
            );
            throw error;
        }
    };

    return (
        <Box display="flex" flexDirection="column" alignItems="center" flexWrap="wrap" margin="1rem">
            <Card className={classes.paperStyle}>
                <Container sx={{ overflow: "auto", padding: "24px" }}>
                    <VBox alignItems="center" sx={{ marginTop: "2rem" }}>
                        <Typography sx={{ fontSize: "2rem", fontWeight: "bold" }}>
                            {t("phoneVer")}
                        </Typography>

                        <img
                            alt="SMS Verification"
                            width="200px"
                            height="120px"
                            src={wrongPinInput ? wrongSmsCode : getSMS}
                        />

                        {!wrongPinInput ? (
                            <>
                                {!verProperties.Resending ? (
                                    <Typography align="center" style={{ marginTop: "1rem", fontSize: "1.3rem" }}>
                                        {t("sendingCodeToPhone")}
                                    </Typography>
                                ) : (
                                    <Typography align="center" style={{ marginTop: "1rem", fontSize: "1.3rem" }}>
                                        {t("resendingCodeToPhone")}
                                        <span style={{ fontWeight: "bold" }}> {t("another")} </span>
                                    </Typography>
                                )}
                                <br />
                                <div style={{ direction: "ltr", marginTop: "1rem", fontSize: "1.3rem", fontWeight: "bold" }}>
                                    {`${t("toPhoneNum")} ${verProperties.phoneText}`}
                                </div>
                                <Typography align="center" display="block" style={{ color: "#3a7735", marginTop: "1rem" }}>
                                    {t("pleaseEnterCode")}
                                </Typography>
                            </>
                        ) : (
                            <>
                                <img
                                    alt="Wrong Code"
                                    width="200px"
                                    height="120px"
                                    src={wrongSmsCode}
                                />
                                <Typography align="center" className={classes.RedColor}>
                                    {t("wrongAuthCode")}
                                </Typography>
                                <Typography align="center" className={classes.paddingBottom1rem}>
                                    {t("reEnterCode")}
                                </Typography>
                            </>
                        )}
                    </VBox>

                    {busy || loading ? (
                        <CustomBusy />
                    ) : (
                        <VBox alignItems="center" style={{ direction: 'ltr', marginTop: "2rem" }}>
                            <ReactCodeInput
                                autoComplete={false}
                                type='number'
                                inputMode="numeric"
                                autoFocus={true}
                                onComplete={handleVerificationChange}
                                fields={6}
                                ref={pinInputRef}
                                color='black'
                                className={classes.codePinInputStyle}
                                disabled={busy || loading}
                            />
                        </VBox>
                    )}

                    <VBox justifyContent="space-around">
                        <VBox alignItems="center" justifyContent="center" marginTop="2rem">
                            {!visible ? (
                                <ReLinkCounter setVisible={setVisible} />
                            ) : (
                                <Link
                                    color="#007f00"
                                    onClick={handleResending}
                                    className={`${classes.CenterItems} ${classes.LinkStyle} ${classes.paddingBottom1rem}`}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    {t("noReceiveToken")}
                                </Link>
                            )}
                        </VBox>
                    </VBox>

                    <Box display="flex" flexDirection="row" justifyContent="left">
                        <Button onClick={() => window.history.back()} className={classes.buttonWhite}>
                            {t("notYourPhone")}
                        </Button>
                    </Box>
                </Container>
            </Card>
        </Box>
    );
};
